import { mapState } from 'vuex';

import CategoryType from '@/helpers/contentCategory';
import { PROCESSING_STATUS } from '@/helpers/avaliaActivity';
import { THUMBNAIL as ThumbnailType } from '@/helpers/contentDesign';
import { SCORM_UPLOAD_STATUS } from '@/helpers/scorm';
import ContentType from '@/helpers/contentType';
import { getTopicSyncStatus } from '~/helpers/topicSyncStatus';
import { syncIconObject } from '@/helpers/contentSync';
import { getAutoGradeStatus, mapAutoGradeStatusTooltip } from '@/helpers/smartIntegrations';

import CourseMixin from '@/mixins/templates/course.js';
import CommonMixin from '@/mixins/templates/common.js';
import ColorAndIconMixin from '@/mixins/content-icon-color.js';
import ValidateTopicConfigurations from '@/mixins/validateTopicConfigurations';
import ContentSyncMixin from '@/mixins/content-sync';

import ListTopics from './list-topics';
import CardInformation from './card-information';
import TopicSettingsMenu from '@/components/content-v2/templates/common/topic-settings-menu';
import RecordingInformation from './recording-information';

export default {
  name   : 'CardTopicStreaming',
  mixins : [CourseMixin, ColorAndIconMixin, CommonMixin, ValidateTopicConfigurations, ContentSyncMixin],
  props  : {
    topic: {
      type    : Object,
      default : () => ({})
    },
    isBlockedContentByConditional: {
      type    : Boolean,
      default : false
    },
    isLastRow: {
      type    : Boolean,
      default : false
    },
    isOpenOnBottomSheet: {
      type    : Boolean,
      default : false
    },
    isPreview: {
      type    : Boolean,
      default : false
    },
    isEditingInGrouper: {
      type    : Boolean,
      default : false
    },
    sequence: {
      type    : Number,
      default : null
    }
  },
  components: {
    ListTopics,
    CardInformation,
    RecordingInformation,
    TopicSettingsMenu
  },
  data() {
    return {
      ContentType,
      ThumbnailType,
      hover                 : false,
      hasExpanded           : false,
      isMobile              : false,
      isMouseOverEditButton : false,
      isExpanded            : false,
      isMenuActivated       : false,
      autoGradeStatusMap    : mapAutoGradeStatusTooltip()
    };
  },
  watch: {
    hover(value) {
      if (this.isLastRow) {
        const element = document.getElementById(this.topic.id);

        if (element) {
          this.$nextTick(() => {
            if (value) {
              const cardTopicWrapper = element.getElementsByClassName('card-topic__wrapper-information');
              if (cardTopicWrapper[0]) {
                const clientHeight = cardTopicWrapper[0].clientHeight;
                element.childNodes[0].style.top = `-${clientHeight - 80}px`;
              }
            } else {
              element.childNodes[0].style.top = '';
            }
          });
        }
      }
    }
  },
  mounted() {
    if (window.innerWidth <= 960) {
      this.isMobile = true;
    }
  },
  computed: {
    ...mapState('plataforma/user', {
      isStudent: state => state.context.isStudent
    }),
    isTopicCompleted() {
      if (this.topic.categoryTypeId === CategoryType.ORGANIZATION) {
        return (
          this.topic.childrenCountWithProgressRecord === this.topic.viewedChildrenCount &&
          this.topic.viewedChildrenCount
        );
      }

      if (this.isAvaliaExternalActivity(this.topic)) {
        if (this.topicHasRetries(this.topic)) {
          return this.studentAnsweredAllAttempts(this.topic) && this.topic.isRecordProgress;
        }
      }

      return this.topic.viewed && this.topic.isRecordProgress;
    },
    isComingSoon() {
      if (this.isPreview) {
        return false;
      }
      if (this.topic.categoryTypeId === CategoryType.ORGANIZATION) {
        return !this.topic.children || !this.topic.children.length;
      }
      if (this.topic.categoryTypeId === CategoryType.WEBCONFERENCE) {
        return !this.topic.content.allowJoinWebconference;
      }
      if (this.topic.categoryTypeId === CategoryType.AVALIA) {
        return (
          this.topic.isFuture || !this.topic.content.isActive || this.topic.content.status !== PROCESSING_STATUS.OK
        );
      }
      if (this.topic.categoryTypeId === CategoryType.SCORM) {
        return this.topic.content.uploadStatus !== SCORM_UPLOAD_STATUS.COMPLETE;
      }
      return false;
    },
    hasTopicStatusBar() {
      return (
        !this.isEditingInGrouper &&
        (this.topic.isBlockedContentByConditional ||
          (this.isStudent && (this.isTopicDeadlineExpired(this.topic) || this.isComingSoon || this.isTopicCompleted || (this.isAvaliaAndIsLockedByAccessCode && this.topicIsAvailableToAnswer(this.topic) && !this.isTopicCompleted))))
      );
    },
    isAvaliaAndIsLockedByAccessCode() {
      return this.topic.topicTypeId === ContentType.AVALIA && this.topic.isAccessCodeEnabled;
    },
    shouldShowAvaliaActivityResultStatus() {
      if (this.isAvaliaExternalActivity(this.topic)) {
        if (this.isAvaliaAndIsLockedByAccessCode && this.topicIsAvailableToAnswer(this.topic)) {
          return false;
        }

        if (this.isTopicDeadlineExpired(this.topic) && this.shouldShowResultActivity(this.topic)) {
          return true;
        }

        if (this.topicHasRetries(this.topic)) {
          return (
            this.mustBeShownFeedback(this.topic) &&
            this.studentAnsweredAllAttempts(this.topic) &&
            this.shouldShowResultActivity(this.topic)
          );
        }

        if (this.mustBeShownFeedback(this.topic)) {
          return this.isTopicFinished(this.topic) && this.shouldShowResultActivity(this.topic);
        }
      }

      return false;
    },
    topicMenuSettingsProps() {
      if (this.isEditingInGrouper || this.isOpenOnBottomSheet) {
        return {};
      }
      return this.isExpanded ? { attach: true, nudgeRight: '50', nudgeTop: '20' } : { attach: true };
    },
    scormStatus() {
      const props = {
        show: false
      };

      if (this.isStudent || this.topic.topicTypeId !== ContentType.SCORM) {
        return props;
      }

      if (this.topic.content.uploadStatus === SCORM_UPLOAD_STATUS.ERROR) {
        props.show = true;
        props.tooltip = this.$t('contentType.scorm.textTooltipTopicError');
        props.color = 'var(--v-error-base)';
        props.icon = 'mdi-alert';
      } else if (this.topic.content.uploadStatus === SCORM_UPLOAD_STATUS.RUNNING) {
        props.show = true;
        props.tooltip = this.$t('contentType.scorm.textTooltipTopicProcessing');
        props.color = 'var(--v-warning-base)';
        props.icon = 'mdi-progress-clock';
      }

      return props;
    },
    isWConferenceNotIntegrated() {
      return this.topic.topicTypeId === ContentType.WEBCONFERENCE && !this.topic.content.isIntegrated;
    },
    isAvaliaAndInactive() {
      return (
        this.topic.topicTypeId === ContentType.AVALIA &&
        (!this.topic.content.isActive || this.topic.content.status !== PROCESSING_STATUS.OK)
      );
    },
    hasAutoGrade() {
      if (this.topic.topicTypeId !== ContentType.TASK) {
        return false;
      }

      return this.topic.hasAutoGrade;
    },
    autoGradeStatusTooltip() {
      const autoGradeResponse = this.topic.content?.autoGradeResponse;
      const autoGradeStatus = autoGradeResponse?.status;

      return getAutoGradeStatus({
        autoGradeResponse,
        autoGradeStatus,
        tooltipPropName       : 'tooltip',
        autoGradeStatusMap    : this.autoGradeStatusMap,
        getterAutoGradeStatus : this.getterAutoGradeStatus
      });
    },
    statusChip() {
      if (this.hasAutoGrade) {
        return this.autoGradeStatusTooltip;
      }

      if (this.isAvaliaAndInactive) {
        return {
          tooltip : this.$t('contentType.avalia.textTooltipTopicAvaliaProcessing'),
          color   : 'var(--v-warning-base)',
          icon    : 'mdi-progress-clock'
        };
      }

      if (this.isWConferenceNotIntegrated) {
        return {
          tooltip : this.$t('contentType.scorm.textTooltipTopicError'),
          color   : 'var(--v-error-base)',
          icon    : 'mdi-alert'
        };
      }
    },
    topicSyncIconObject() {
      let syncStatus = null;

      if (this.isMasterCourse && this.topic && this.getterCourse.associatedAcademicMains) {
        syncStatus = getTopicSyncStatus({
          topic                   : this.topic,
          topicCategoryType       : this.topic.categoryTypeId,
          associatedAcademicMains : this.getterCourse.associatedAcademicMains
        });
      }

      return syncStatus ? syncIconObject[syncStatus] : null;
    }
  },
  methods: {
    showConditionalAvailability() {
      this.$nuxt.$emit('changeIsOpenConditionalsListModal', {
        conditionals        : this.topic.conditionals,
        isAccessCodeEnabled : this.topic.isAccessCodeEnabled
      });
    },
    getColorByTopicTypeId() {
      if (this.topicIsHidden(this.topic)) {
        return 'var(--v-neutrals-300-base)';
      } else if (this.topic.design.cardDetailsColor) {
        return this.topic.design.cardDetailsColor;
      } else {
        return this.getColor(this.topic.topicTypeId);
      }
    },
    getIconByRequestOrByDefault() {
      return this.topic.design.thumbnailDesignId === ThumbnailType.ICON && this.topic.design.thumbnailValue
        ? this.topic.design.thumbnailValue
        : this.getIcon(this.topic.topicTypeId);
    },
    getTopicTypeName() {
      if (this.topic.topicTypeId === ContentType.PAGE) {
        const childrenLength = this.topic.children ? this.topic.children.length : 0;
        return this.$tc('topicTypes.learningUnitWithItems', childrenLength, { count: childrenLength });
      }

      const topicTypeNameKey = ContentType.getInternationalizationTopicTypeKeyNameById(this.topic.topicTypeId);
      return this.$t(topicTypeNameKey);
    },
    managementHasExpanded() {
      this.hasExpanded = true;

      if (this.isOpenOnBottomSheet && this.hasWebConferenceProviderWithRecordings(this.topic) && this.hasExpanded) {
        this.$nextTick(() => {
          this.$refs.webconference.panel = [0];

          this.$emit('hasExpanded');

          this.$refs.webconference.getWebConferenceRecords();
        });
      } else {
        this.$emit('hasExpanded');
      }
    },
    setFalseIsMouseOverEditButton() {
      setTimeout(() => {
        this.isMouseOverEditButton = false;
        if (!this.isMenuActivated) {
          this.isExpanded = this.hover;
        }
      }, 600);
    },
    handleHoverChange(value) {
      if (value) {
        this.isExpanded = !this.isMouseOverEditButton;
        return;
      }
      this.isExpanded = false;
    },
    openAvaliaActivityResults(topic) {
      const params = {
        destinationPage                  : 'results',
        classExternalId                  : this.getterCourse.context.academicMainExternalId,
        activityExternalSource           : topic.content.activityExternalSource,
        questionCollectionExternalSource : topic.content.questionCollectionExternalSource,
        activityExternalId               : topic.content.activityExternalId,
        questionCollectionExternalId     : topic.content.questionCollectionExternalId
      };

      this.callAvaliaSSO(params, topic);
    }
  }
};
